import Header from "@/pages/components/Header";
import Footer from "@/pages/components/Footer";
import WorkCard from "@/pages/components/WorkCard";
import { categories, works, faqs, heroImage } from "@/mocks/manhwaData";

const sortedByPopularity = [...works].sort((a, b) => b.popularity - a.popularity);
const topRanked = [...works].sort((a, b) => b.rating - a.rating).slice(0, 4);
const newReleases = works.filter((w) => w.status === "连载中").slice(0, 4);

function SectionHeading({
  eyebrow,
  title,
  desc,
  link,
  linkLabel,
}: {
  eyebrow: string;
  title: string;
  desc: string;
  link?: string;
  linkLabel?: string;
}) {
  return (
    <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
          <i className="ri-fire-line"></i>
          {eyebrow}
        </span>
        <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">{title}</h2>
        <p className="mt-2 max-w-xl text-sm text-foreground-600">{desc}</p>
      </div>
      {link && linkLabel && (
        <a
          href={link}
          className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
        >
          {linkLabel}
          <i className="ri-arrow-right-line"></i>
        </a>
      )}
    </div>
  );
}

export default function Home() {
  return (
    <>
      <Header />

      <main>
        {/* Hero */}
        <section className="relative flex min-h-[560px] w-full items-center justify-center overflow-hidden">
          <img
            src={heroImage}
            alt="正版韩漫大全热门韩漫推荐背景插画"
            title="正版韩漫大全 · 题材分类聚合"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          <div className="relative z-10 mx-auto w-full max-w-4xl px-4 py-20 text-center md:px-6">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-medium text-background-50 backdrop-blur-sm">
              <i className="ri-verified-badge-fill text-secondary-300"></i>
              正版授权 · 分类榜单聚合
            </span>
            <h1 className="mt-6 font-heading text-3xl leading-tight text-background-50 md:text-5xl md:leading-tight">
              正版韩漫大全
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
              从甜宠到悬疑、从奇幻到现实，全题材韩漫分类聚合。按类型、人气、榜单多维度整理推荐，热门分类与最新人气推荐集中展示，一页告别选择困难。
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="#categories"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-grid-fill"></i>
                浏览题材分类
              </a>
              <a
                href="#ranking"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50/15 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/25"
              >
                <i className="ri-trophy-line"></i>
                查看人气榜单
              </a>
            </div>
            <div className="mx-auto mt-10 grid max-w-lg grid-cols-3 gap-3">
              {[
                { num: "760+", label: "正版韩漫作品" },
                { num: "8 大", label: "核心题材分类" },
                { num: "24h", label: "榜单实时更新" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl bg-background-50/10 px-3 py-4 backdrop-blur-sm"
                >
                  <p className="font-heading text-xl text-background-50 md:text-2xl">{s.num}</p>
                  <p className="mt-1 text-xs text-background-100/80">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section id="categories" className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="题材聚合"
            title="核心分类一页全览"
            desc="八大热门题材，从甜宠到悬疑、从奇幻到现实，一键直达你想看的类型。"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={`/categories#${cat.id}`}
                className="group relative overflow-hidden rounded-2xl border border-background-200/70"
              >
                <img
                  src={cat.cover}
                  alt={`${cat.name}韩漫分类`}
                  title={`${cat.name} · 韩漫题材分类`}
                  className="h-40 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105 md:h-44"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-4">
                  <div>
                    <h3 className="font-heading text-lg text-background-50">{cat.name}</h3>
                    <p className="mt-0.5 text-xs text-background-100/80">{cat.desc}</p>
                  </div>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors group-hover:bg-primary-500">
                    <i className={cat.icon}></i>
                  </span>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Ranking */}
        <section id="ranking" className="bg-background-100/70 border-y border-background-200/70">
          <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <SectionHeading
              eyebrow="人气榜单"
              title="本周最受欢迎的韩漫"
              desc="综合人气值、评分与完读率排序，实时反映当下最热门的韩漫作品。"
            />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
              <div className="lg:col-span-3">
                <div className="space-y-3">
                  {sortedByPopularity.slice(0, 5).map((work, i) => (
                    <a
                      key={work.id}
                      href={`/categories#${work.categoryId}`}
                      className="flex items-center gap-4 rounded-2xl bg-background-50 p-3 transition-colors hover:bg-background-50/80"
                    >
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-heading text-lg ${
                          i === 0
                            ? "bg-primary-500 text-background-50"
                            : i === 1
                            ? "bg-secondary-500 text-background-50"
                            : i === 2
                            ? "bg-accent-500 text-background-50"
                            : "bg-secondary-100 text-secondary-900"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <div className="relative h-20 w-14 shrink-0 overflow-hidden rounded-lg">
                        <img
                          src={work.cover}
                          alt={`${work.title}人气韩漫`}
                          title={`${work.title} · 人气韩漫排名`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-semibold text-foreground-950 md:text-base">
                          {work.title}
                        </h3>
                        <p className="mt-1 truncate text-xs text-foreground-500">
                          {work.categoryName} · {work.status} · {work.chapters}话
                        </p>
                      </div>
                      <div className="hidden text-right sm:block">
                        <p className="text-sm font-semibold text-primary-600">
                          {work.popularity} 人气
                        </p>
                        <p className="mt-0.5 flex items-center justify-end gap-0.5 text-xs text-secondary-600">
                          <i className="ri-star-fill"></i>
                          {work.rating.toFixed(1)}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="h-full rounded-2xl bg-primary-500 p-6 md:p-7">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-50">
                    <i className="ri-flashlight-line"></i>
                    编辑力荐
                  </span>
                  <h3 className="mt-4 font-heading text-2xl text-background-50 md:text-3xl">
                    高分口碑之作
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-background-100/90">
                    以下是近期评分最高的四部人气韩漫，无论你是甜宠爱好者还是悬疑迷，都能在这里找到被众多读者反复安利的高分作品。
                  </p>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {topRanked.map((work) => (
                      <a key={work.id} href={`/categories#${work.categoryId}`} className="group">
                        <div className="relative aspect-[3/4] overflow-hidden rounded-xl">
                          <img
                            src={work.cover}
                            alt={`${work.title}高分韩漫封面`}
                            title={`${work.title} · 高分口碑韩漫`}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                          <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-background-50/90 text-xs font-bold text-primary-600">
                            {work.rating.toFixed(1)}
                          </span>
                        </div>
                        <p className="mt-2 truncate text-sm text-background-50">{work.title}</p>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Recommendations */}
        <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="人气推荐"
            title="今日人气推荐"
            desc="编辑精选的高人气韩漫，覆盖甜宠、奇幻、悬疑、热血等多样题材。"
            link="/categories"
            linkLabel="查看全部分类"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {works.slice(0, 8).map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </section>

        {/* New releases */}
        <section className="bg-background-100/70 border-y border-background-200/70">
          <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <SectionHeading
              eyebrow="新作速递"
              title="正在热播的连载韩漫"
              desc="第一时间掌握正在更新的连载新作，追更不迷路。"
              link="/rankings"
              linkLabel="查看人气榜单"
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {newReleases.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto w-full max-w-4xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-8 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              <i className="ri-question-line"></i>
              常见问题
            </span>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">
              关于正版韩漫大全，你想知道的都在这里
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <details
                key={faq.q}
                className="group rounded-2xl border border-background-200/70 bg-background-100 px-5 py-4"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-foreground-950 md:text-base">
                    {faq.q}
                  </h3>
                  <i className="ri-add-line text-lg text-primary-500 transition-transform group-open:rotate-45"></i>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}